import { useCallback, useEffect, useState } from 'react';
import type { Locale } from 'date-fns';
import {
  differenceInMinutes,
  formatDistanceToNowStrict,
  isFuture,
  isValid,
  toDate,
} from 'date-fns';
import { enUS } from 'date-fns/locale';
import { getIntl, intl, plural, select, withIntl } from '@frameio/i18n';
import TEXT from './generated/TEXT/useFormattedRelativeDate';

TEXT.DEFINE(
  {
    /** Shown for very recent times (less than a minute or configured threshold) */
    justNow: 'Just now',
    /** Half a minute */
    halfMinute: 'half a minute',
    /** Half a minute - truncated */
    halfMinuteShort: 'half a m',

    /** Relative time in the past (e.g., "2 hours ago", "1 day ago") */
    timeAgo: ({ unit, count }: { unit: string; count: number }): string =>
      select(unit, {
        second: plural(count, {
          zero: 'Just now',
          one: '1 second ago',
          other: `${count} seconds ago`,
        }),
        minute: plural(count, {
          one: '1 minute ago',
          other: `${count} minutes ago`,
        }),
        hour: plural(count, { one: '1 hour ago', other: `${count} hours ago` }),
        day: plural(count, { one: '1 day ago', other: `${count} days ago` }),
        week: plural(count, { one: '1 week ago', other: `${count} weeks ago` }),
        month: plural(count, {
          one: '1 month ago',
          other: `${count} months ago`,
        }),
        year: plural(count, { one: '1 year ago', other: `${count} years ago` }),
        other: '',
      }),

    /** Relative time in the future (e.g., "in 2 hours", "in 1 day") */
    timeFromNow: ({ unit, count }: { unit: string; count: number }): string =>
      select(unit, {
        second: plural(count, {
          one: 'in 1 second',
          other: `in ${count} seconds`,
        }),
        minute: plural(count, {
          one: 'in 1 minute',
          other: `in ${count} minutes`,
        }),
        hour: plural(count, { one: 'in 1 hour', other: `in ${count} hours` }),
        day: plural(count, { one: 'in 1 day', other: `in ${count} days` }),
        week: plural(count, { one: 'in 1 week', other: `in ${count} weeks` }),
        month: plural(count, {
          one: 'in 1 month',
          other: `in ${count} months`,
        }),
        year: plural(count, { one: 'in 1 year', other: `in ${count} years` }),
        other: '',
      }),

    /** Truncated relative time (e.g., "2h", "1d", "3mo") */
    timeShort: ({ unit, count }: { unit: string; count: number }): string =>
      select(unit, {
        second: `${count}s`,
        minute: `${count}m`,
        hour: `${count}h`,
        day: `${count}d`,
        week: `${count}w`,
        month: `${count}mo`,
        year: `${count}y`,
        other: '',
      }),

    /** Approximate truncated time (e.g., "about 2h", "about 1w") */
    aboutTimeShort: ({
      unit,
      count,
    }: {
      unit: string;
      count: number;
    }): string =>
      select(unit, {
        hour: plural(count, { one: 'about 1h', other: `about ${count}h` }),
        week: plural(count, { one: 'about 1w', other: `about ${count}w` }),
        month: plural(count, { one: 'about 1mo', other: `about ${count}mo` }),
        year: plural(count, { one: 'about 1y', other: `about ${count}y` }),
        other: '',
      }),

    /** Less than specified time - truncated (e.g., "less than 2m") */
    lessThanTimeShort: ({
      unit,
      count,
    }: {
      unit: string;
      count: number;
    }): string =>
      select(unit, {
        minute: plural(count, {
          one: 'less than a m',
          other: `less than ${count}m`,
        }),
        other: '',
      }),

    /** Over specified years - truncated (e.g., "over 1y", "over 2y") */
    overYearsShort: (count: number): string =>
      plural(count, { one: 'over 1y', other: `over ${count}y` }),

    /** Almost specified years - truncated (e.g., "almost 1y", "almost 2y") */
    almostYearsShort: (count: number): string =>
      plural(count, { one: 'almost 1y', other: `almost ${count}y` }),

    /** Full relative time without suffix (e.g., "2 days", "3 hours") */
    timeFull: ({ unit, count }: { unit: string; count: number }): string =>
      select(unit, {
        second: plural(count, { one: '1 second', other: `${count} seconds` }),
        minute: plural(count, { one: '1 minute', other: `${count} minutes` }),
        hour: plural(count, { one: '1 hour', other: `${count} hours` }),
        day: plural(count, { one: '1 day', other: `${count} days` }),
        week: plural(count, { one: '1 week', other: `${count} weeks` }),
        month: plural(count, { one: '1 month', other: `${count} months` }),
        year: plural(count, { one: '1 year', other: `${count} years` }),
        other: '',
      }),

    /** Past time with suffix. Reorder {time} placeholder for language word order. */
    withSuffixPast: ({ time }: { time: string }): string => `${time} ago`,

    /** Future time with suffix. Reorder {time} placeholder for language word order. */
    withSuffixFuture: ({ time }: { time: string }): string =>
      `${time} from now`,
  },
  { plural, select }
);

/** Valid tokens from date-fns formatDistance */
type LocaleToken =
  | 'lessThanXSeconds'
  | 'xSeconds'
  | 'halfAMinute'
  | 'lessThanXMinutes'
  | 'xMinutes'
  | 'aboutXHours'
  | 'xHours'
  | 'xDays'
  | 'aboutXWeeks'
  | 'xWeeks'
  | 'aboutXMonths'
  | 'xMonths'
  | 'aboutXYears'
  | 'xYears'
  | 'overXYears'
  | 'almostXYears';

/** Pre-resolved TEXT values for use inside hooks (avoids calling TEXT getters in callbacks) */
type TruncatedTextValues = {
  justNow: string;
  halfMinuteShort: string;
  timeShort: (args: { unit: string; count: number }) => string;
  aboutTimeShort: (args: { unit: string; count: number }) => string;
  lessThanTimeShort: (args: { unit: string; count: number }) => string;
  overYearsShort: (count: number) => string;
  almostYearsShort: (count: number) => string;
};

/** Pre-resolved TEXT values for full format */
type FullTextValues = {
  justNow: string;
  halfMinute: string;
  timeFull: (args: { unit: string; count: number }) => string;
  withSuffixPast: (args: { time: string }) => string;
  withSuffixFuture: (args: { time: string }) => string;
};

type IntlContext = ReturnType<typeof getIntl>;

/**
 * Bind a TEXT formatter to a captured intl context so it resolves the correct
 * locale even when invoked off the render path (e.g. inside the date-fns
 * formatDistance callback fired from setInterval). Without this, the formatter
 * falls back to its English default when called outside of render.
 */
const bindTextFormatter =
  <TArgs extends unknown[], TResult>(
    intlContext: IntlContext,
    formatter: (...args: TArgs) => TResult
  ) =>
  (...args: TArgs) =>
    withIntl(intlContext, formatter, ...args);

/**
 * Create a formatDistance function using pre-resolved TEXT values.
 * This avoids calling TEXT getters inside hooks/callbacks.
 */
const createFormatTruncatedDistance =
  (textValues: TruncatedTextValues) =>
  (token: LocaleToken, count: number): string => {
    switch (token) {
      case 'lessThanXSeconds':
      case 'xSeconds':
        return textValues.justNow;
      case 'halfAMinute':
        return textValues.halfMinuteShort;
      case 'lessThanXMinutes':
        return textValues.lessThanTimeShort({ unit: 'minute', count });
      case 'xMinutes':
        return textValues.timeShort({ unit: 'minute', count });
      case 'aboutXHours':
        return textValues.aboutTimeShort({ unit: 'hour', count });
      case 'xHours':
        return textValues.timeShort({ unit: 'hour', count });
      case 'xDays':
        return textValues.timeShort({ unit: 'day', count });
      case 'aboutXWeeks':
        return textValues.aboutTimeShort({ unit: 'week', count });
      case 'xWeeks':
        return textValues.timeShort({ unit: 'week', count });
      case 'aboutXMonths':
        return textValues.aboutTimeShort({ unit: 'month', count });
      case 'xMonths':
        return textValues.timeShort({ unit: 'month', count });
      case 'aboutXYears':
        return textValues.aboutTimeShort({ unit: 'year', count });
      case 'xYears':
        return textValues.timeShort({ unit: 'year', count });
      case 'overXYears':
        return textValues.overYearsShort(count);
      case 'almostXYears':
        return textValues.almostYearsShort(count);
      default:
        return '';
    }
  };

/**
 * Create a truncated locale with pre-resolved TEXT values.
 * Must be called at render level (not inside hooks).
 */
const createTruncatedLocale = (textValues: TruncatedTextValues): Locale => ({
  ...enUS,
  formatDistance: createFormatTruncatedDistance(textValues),
});

/** Maps date-fns tokens to full TEXT values */
const createFormatFullDistance =
  (textValues: FullTextValues) =>
  (token: LocaleToken, count: number): string => {
    switch (token) {
      case 'lessThanXSeconds':
      case 'xSeconds':
        return textValues.timeFull({ unit: 'second', count });
      case 'halfAMinute':
        return textValues.halfMinute;
      case 'xMinutes':
        return textValues.timeFull({ unit: 'minute', count });
      case 'xHours':
      case 'aboutXHours':
        return textValues.timeFull({ unit: 'hour', count });
      case 'xDays':
        return textValues.timeFull({ unit: 'day', count });
      case 'xWeeks':
      case 'aboutXWeeks':
        return textValues.timeFull({ unit: 'week', count });
      case 'xMonths':
      case 'aboutXMonths':
        return textValues.timeFull({ unit: 'month', count });
      case 'xYears':
      case 'aboutXYears':
      case 'overXYears':
      case 'almostXYears':
        return textValues.timeFull({ unit: 'year', count });
      default:
        return '';
    }
  };

/** Creates a date-fns locale using full TEXT values */
const createFullLocale = (textValues: FullTextValues): Partial<Locale> => ({
  formatDistance: createFormatFullDistance(textValues),
});

/**
 * Get a truncated locale with localized TEXT values.
 * MUST be called at React render level (not inside hooks/callbacks) since TEXT uses hooks internally.
 *
 * @example
 * // In a React component:
 * const truncatedLocale = getTruncatedLocale();
 * const timeStr = formatDistanceStrict(date1, date2, { locale: truncatedLocale });
 */
export const getTruncatedLocale = (): Locale => {
  const textValues = getTruncatedTextValues();
  return createTruncatedLocale(textValues);
};

/**
 * Get a full locale with localized TEXT values.
 * MUST be called at React render level (not inside hooks/callbacks) since TEXT uses hooks internally.
 */
export const getFullLocale = (): Locale => {
  const textValues = getFullTextValues();
  return createFullLocale(textValues);
};

/**
 * Pre-resolve all truncated TEXT values at render level.
 * This captures the TEXT getter results before any hooks are called.
 */
const getTruncatedTextValues = (
  intlContext: IntlContext = getIntl()
): TruncatedTextValues => ({
  justNow: TEXT.justNow,
  halfMinuteShort: TEXT.halfMinuteShort,
  timeShort: bindTextFormatter(intlContext, TEXT.timeShort),
  aboutTimeShort: bindTextFormatter(intlContext, TEXT.aboutTimeShort),
  lessThanTimeShort: bindTextFormatter(intlContext, TEXT.lessThanTimeShort),
  overYearsShort: bindTextFormatter(intlContext, TEXT.overYearsShort),
  almostYearsShort: bindTextFormatter(intlContext, TEXT.almostYearsShort),
});

const getFullTextValues = (
  intlContext: IntlContext = getIntl()
): FullTextValues => ({
  justNow: TEXT.justNow,
  halfMinute: TEXT.halfMinute,
  timeFull: bindTextFormatter(intlContext, TEXT.timeFull),
  withSuffixPast: bindTextFormatter(intlContext, TEXT.withSuffixPast),
  withSuffixFuture: bindTextFormatter(intlContext, TEXT.withSuffixFuture),
});

type FormatDateDistanceToNowOptions = Pick<
  RelativeDateFormatOptions,
  'date' | 'keepJustNowForMins' | 'truncate'
> & {
  truncatedTextValues: TruncatedTextValues;
  fullTextValues: FullTextValues;
};

/**
 * Format a date relative to now (e.g., "2 months" or "2mo" when truncated).
 * Returns null for invalid dates.
 * @internal Use within useFormattedRelativeDate hook only.
 */
const formatDateDistanceToNowInternal = ({
  date,
  keepJustNowForMins,
  truncate,
  truncatedTextValues,
  fullTextValues,
}: FormatDateDistanceToNowOptions): string | null => {
  const dateValue = typeof date === 'string' ? new Date(date) : date;

  const isInvalidDate =
    !dateValue ||
    dateValue.toString() === 'Invalid Date' ||
    !isValid(dateValue);

  if (isInvalidDate) return null;

  const shouldRenderJustNow = Boolean(
    keepJustNowForMins &&
      differenceInMinutes(Date.now(), toDate(date)) < keepJustNowForMins
  );

  if (shouldRenderJustNow) {
    return truncatedTextValues.justNow;
  }

  const localeToUse = truncate
    ? createTruncatedLocale(truncatedTextValues)
    : createFullLocale(fullTextValues);

  return formatDistanceToNowStrict(toDate(date), {
    locale: localeToUse,
  });
};

type FormatDateDistanceToNow = Pick<
  RelativeDateFormatOptions,
  'date' | 'keepJustNowForMins' | 'truncate'
>;

/**
 * Format a date relative to now (e.g., "2 months" or "2mo" when truncated).
 * Returns null for invalid dates.
 * WARNING: This accesses TEXT which uses React hooks - only call at render level,
 * never inside useEffect, useMemo, useCallback, or other hooks.
 */
export const formatDateDistanceToNow = ({
  date,
  keepJustNowForMins = defaultRelativeDateFormatOptions.keepJustNowForMins,
  truncate = defaultRelativeDateFormatOptions.truncate,
}: FormatDateDistanceToNow): string | null => {
  const intlContext = intl();
  // Pre-resolve TEXT values at call time (must be at render level)
  const truncatedTextValues = getTruncatedTextValues(intlContext);
  const fullTextValues = getFullTextValues(intlContext);

  return formatDateDistanceToNowInternal({
    date,
    keepJustNowForMins,
    truncate,
    truncatedTextValues,
    fullTextValues,
  });
};

/**
 * Lowercase the leading character so a localized, capitalized relative date
 * reads naturally when embedded mid-sentence (e.g. fr "Il y a 6 mois" inside
 * "Mise à jour {x}"). Opt in via the `lowercaseInitial` option; standalone
 * consumers (table cells, status chips) keep the capital.
 */
export const lowercaseInitial = (value: string) =>
  value ? value.charAt(0).toLowerCase() + value.slice(1) : value;

type RelativeDateFormatOptions = {
  date: number | Date;
  /* Replaces normal spaces with non-breaking spaces, keeping the whole string on one line */
  preventLineBreaks?: boolean;
  /** returns a suffix "from now" on only future dates past the 60 seconds mark */
  showFutureSuffix?: boolean;
  /* return a suffix 'ago' or 'from now' */
  showSuffix?: boolean;
  /* return a shortened suffix 'months' ==>. 'mo' */
  truncate?: boolean;
  /* optionally skip re-calculating relative distance on interval */
  skipInterval?: boolean;
  /* optionally keep rendering 'just now' for the amount of minutes */
  keepJustNowForMins?: number;
  /* lowercase the leading character for mid-sentence embedding (see lowercaseInitial) */
  lowercaseInitial?: boolean;
};

const defaultRelativeDateFormatOptions = {
  preventLineBreaks: true,
  showFutureSuffix: true,
  showSuffix: true,
  truncate: false,
  skipInterval: false,
  keepJustNowForMins: 0,
  lowercaseInitial: false,
};

/**
 * This takes in a date and returns a string value representing a human-readable
 * relative date format between the given date and now
 */
export const useFormattedRelativeDate = (
  options: RelativeDateFormatOptions
): string => {
  const {
    date: initialDateValue,
    preventLineBreaks,
    showFutureSuffix,
    showSuffix,
    truncate,
    skipInterval,
    keepJustNowForMins,
    lowercaseInitial: shouldLowercaseInitial,
  } = {
    ...defaultRelativeDateFormatOptions,
    ...options,
  };

  const dateValue =
    typeof initialDateValue === 'string'
      ? new Date(initialDateValue)
      : initialDateValue;

  const isInvalidDate =
    !dateValue ||
    dateValue.toString() === 'Invalid Date' ||
    !isValid(dateValue);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const date = isInvalidDate ? new Date() : dateValue;
  const dateIsFuture = isFuture(toDate(date));
  const nbsp = '\xA0';

  // Pre-resolve all TEXT values at render level BEFORE any hooks.
  // TEXT getters use React hooks internally, so they cannot be called
  // inside useState, useCallback, useEffect, or other hooks.
  // Note: textValues is recreated each render but the content (string/function refs) is stable.
  const intlContext = intl();
  const intlLocale = intlContext.locale;
  const truncatedTextValues = getTruncatedTextValues(intlContext);
  const fullTextValues = getFullTextValues(intlContext);
  const justNowText = truncatedTextValues.justNow;

  // Compute initial value at hook top-level using pre-resolved text values
  const initialRelativeDate = formatDateDistanceToNowInternal({
    date,
    keepJustNowForMins,
    truncate,
    truncatedTextValues,
    fullTextValues,
  });

  // Note: textValues changes reference each render, causing callback recreation.
  // This is acceptable as the actual format result is stable.
  const formatDateDistance = useCallback(
    () =>
      formatDateDistanceToNowInternal({
        date,
        keepJustNowForMins,
        truncate,
        truncatedTextValues,
        fullTextValues,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [date, truncate, keepJustNowForMins, intlLocale]
  );

  const [currentRelativeDate, setCurrentRelativeDate] =
    useState(initialRelativeDate);

  useEffect(() => {
    setCurrentRelativeDate(formatDateDistance());

    const difInMins = Math.abs(differenceInMinutes(toDate(date), Date.now()));
    let intervalTiming = 1000;

    if (difInMins > 1 || (difInMins < 1 && truncate)) {
      intervalTiming = 1000 * 60;
    }

    if (difInMins > 60) {
      intervalTiming = 1000 * 60 * 60;
    }

    let dateInterval: ReturnType<typeof setInterval> | undefined;

    if (!skipInterval) {
      dateInterval = setInterval(() => {
        setCurrentRelativeDate(formatDateDistance());
      }, intervalTiming);
    }

    return () => clearInterval(dateInterval);
  }, [formatDateDistance, date, truncate, skipInterval]);

  if (isInvalidDate) {
    return '';
  }

  const isJustNow = currentRelativeDate === justNowText;

  let formattedDate = currentRelativeDate ?? '';

  // Handle suffix logic
  // - showSuffix controls whether we add "ago" (past) or "from now" (future)
  // - showFutureSuffix specifically controls whether future dates get "from now"
  // - When showSuffix=true but showFutureSuffix=false, past dates get "ago" but future dates get no suffix
  // - When showSuffix=false but showFutureSuffix=true, only future dates get "from now"
  if (!isJustNow) {
    if (dateIsFuture) {
      // Future date: only add "from now" if showFutureSuffix is true
      if (showFutureSuffix) {
        formattedDate = fullTextValues.withSuffixFuture({
          time: formattedDate,
        });
      }
    } else if (showSuffix) {
      // Past date: add "ago" if showSuffix is true
      formattedDate = fullTextValues.withSuffixPast({ time: formattedDate });
    }
  }

  if (preventLineBreaks) {
    formattedDate = formattedDate.replace(/ /g, nbsp);
  }

  if (shouldLowercaseInitial) {
    formattedDate = lowercaseInitial(formattedDate);
  }

  return formattedDate;
};
